import React, { useEffect, useState } from "react";
import { Paper, Box, Typography, Button, IconButton, Slide, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import IosShareIcon from "@mui/icons-material/IosShare";
import { useTranslation } from "react-i18next";

// Evento beforeinstallprompt (non tipizzato nella lib standard).
interface BIPEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "pwaInstallDismissedAt";
const DISMISS_DAYS = 7;

// Banner di installazione PWA: su Android/Chrome usa il prompt nativo (beforeinstallprompt);
// su iPhone/iPad (Safari, niente prompt nativo) mostra le istruzioni "Condividi → Aggiungi a Home".
// Si nasconde se l'app è già installata (standalone) o se rifiutato di recente.
export function InstallPrompt() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [open, setOpen] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    // Già installata (aperta come app)? Non mostrare.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (standalone) return;

    // Rifiutato di recente? Rispetta la scelta per qualche giorno.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 3600 * 1000) return;

    const ua = navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua) || (/(macintosh)/i.test(ua) && "ontouchend" in document);
    const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios|android/i.test(ua);
    if (isIOS && isSafari) {
      // iOS Safari: nessun prompt nativo → istruzioni manuali.
      setIos(true);
      setOpen(true);
      return;
    }

    // Evento già catturato all'avvio (vedi main.tsx)? Usalo subito.
    const early = globalThis.__kappaInstallPrompt as BIPEvent | null;
    if (early) { setDeferred(early); setOpen(true); }

    const useEvent = () => {
      const ev = globalThis.__kappaInstallPrompt as BIPEvent | null;
      if (ev) { setDeferred(ev); setOpen(true); }
    };
    const onBIP = (e: Event) => { e.preventDefault(); setDeferred(e as BIPEvent); setOpen(true); };
    const onInstalled = () => { globalThis.__kappaInstallPrompt = null; setOpen(false); setDeferred(null); };
    window.addEventListener("kappa-installable", useEvent);
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("kappa-installable", useEvent);
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch { /* ignora */ }
    setDeferred(null);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1400,
          p: 2, borderTopLeftRadius: 16, borderTopRightRadius: 16,
          display: "flex", alignItems: "center", gap: 1.5,
          maxWidth: 560, mx: "auto"
        }}
      >
        <Avatar src="/icon-192.png" variant="rounded" sx={{ width: 44, height: 44 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800}>{t("install.title")}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {ios ? t("install.iosHint") : t("install.message")}
          </Typography>
        </Box>
        {ios ? (
          <IosShareIcon color="primary" />
        ) : (
          <Button variant="contained" size="small" startIcon={<InstallMobileIcon />} onClick={install} sx={{ flexShrink: 0 }}>
            {t("install.action")}
          </Button>
        )}
        <IconButton size="small" onClick={dismiss} aria-label={t("install.later")}><CloseIcon fontSize="small" /></IconButton>
      </Paper>
    </Slide>
  );
}
